// src/pages/produtos/produtos/ProdutoSearchDelivery.js
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import { FaSortUp, FaSortDown, FaFilter, FaPlus, FaTrash } from "react-icons/fa";
import { formatNumeric } from '../../../utils/numberUtils';
import LazyLoading from '../../../components/modalForm/LazyLoading';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalTable from '../../../components/modalForm/ModalTable';
import ModalSearchBar from '../../../components/modalForm/ModalSearchBar';
import ModalSelectButton from '../../../components/modalForm/ModalSelectButton';
import { ModalActionButton } from '../../../components/modalForm/ModalActions';
import InputCheck from '../../../components/input/InputCheck';
import ProdutoSearch from './ProdutoSearch';
import './ProdutoSearchDelivery.css';

// Função para remover acentos
// Normalização centralizada para manter as pesquisas de produto consistentes.
import { normalizarTextoProduto, produtoCorrespondeFiltro } from '../../../services/produtos/ProdutoFiltroService';

const LS_KEY_HIDDEN_GROUPS = "produtoSearchDelivery_hiddenGroupIds";

const ProdutoSearchDelivery = ({ show, onHide, onSelectProduto }) => {
  const { produtos, produtosFavoritos, alteraFavorito } = useProduto();
  const [produtoFilter, setProdutoFilter] = useState('');
  const [grupoFilter, setGrupoFilter] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(-1);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [listarInativos, setListarInativos] = useState(false);
  const [exibirFavoritos, setExibirFavoritos] = useState(false);
  const [showFavoritosModal, setShowFavoritosModal] = useState(false);
  const [showAdicionarFavoritoModal, setShowAdicionarFavoritoModal] = useState(false);

  // ✅ Modal de seleção multi de grupos
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [hiddenGroupIds, setHiddenGroupIds] = useState(new Set()); // ids ocultos

  const produtoInputRef = useRef(null);
  const groupTableRef = useRef(null);
  const productTableRef = useRef(null);

  useEffect(() => {
    if (show) {
        setProdutoFilter('');
        setGrupoFilter('');
        setSelectedGroup(null);
        setSelectedGroupIndex(-1);
        setListarInativos(false);
        setExibirFavoritos(false);
        setShowGroupModal(false);
        setShowFavoritosModal(false);
        setShowAdicionarFavoritoModal(false);

        // ✅ tenta restaurar seleção salva
        try {
        const raw = localStorage.getItem(LS_KEY_HIDDEN_GROUPS);
        if (raw) {
            const ids = JSON.parse(raw);
            if (Array.isArray(ids) && ids.length > 0) {
            setHiddenGroupIds(new Set(ids));
            } else {
            setHiddenGroupIds(new Set());
            }
        } else {
            setHiddenGroupIds(new Set());
        }
        } catch {
        setHiddenGroupIds(new Set());
        }

        if (produtoInputRef.current) {
        produtoInputRef.current.focus();
        }
    }
    }, [show]);

  // Efeito para manter o grupo selecionado visível na grade
  useEffect(() => {
    if (selectedGroupIndex >= 0 && groupTableRef.current) {
      const selectedRow = selectedGroup
        ? groupTableRef.current.querySelector(`tr:nth-child(${selectedGroupIndex + 3})`)
        : groupTableRef.current.querySelector('tr:first-child');
      if (selectedRow) {
        selectedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedGroupIndex]);

  // ==========================
// ✅ Grupos (da pesquisa)
// ==========================
const grupos = useMemo(() => {
  const gruposMap = new Map();
  produtos.forEach(produto => {
    if (produto.grupo && (listarInativos || (!produto.grupo.inativo && !produto.inativo))) {
      gruposMap.set(produto.grupo.id, produto.grupo);
    }
  });

  return Array.from(gruposMap.values())
    .filter(grupo =>
      normalizarTextoProduto(grupo?.descricao)
        .includes(normalizarTextoProduto(grupoFilter))
    )
    .sort((a, b) =>
      normalizarTextoProduto(a.descricao)
        .localeCompare(normalizarTextoProduto(b.descricao))
    );
}, [produtos, listarInativos, grupoFilter]);

// ✅ grupos que aparecem na tabela da esquerda
const gruposVisiveis = useMemo(() => {
  return grupos.filter(g => !hiddenGroupIds.has(g.id));
}, [grupos, hiddenGroupIds]);



// ✅ AGORA sim: return condicional
if (!show) return null;



const salvarFiltroGrupos = () => {
  if (hiddenGroupIds.size > 0) {
    localStorage.setItem(LS_KEY_HIDDEN_GROUPS, JSON.stringify(Array.from(hiddenGroupIds)));
  } else {
    localStorage.removeItem(LS_KEY_HIDDEN_GROUPS);
  }

  // ✅ ao aplicar o modal, remove seleção única
  setSelectedGroup(null);
  setSelectedGroupIndex(-1);

  setShowGroupModal(false);
};


  // ==========================
  // ✅ Filtro de produtos
  // ==========================
  const produtoIdFilter = produtoFilter.trim();

  let filteredProdutos = (exibirFavoritos ? produtosFavoritos : produtos).filter(produto =>
    (
      produtoCorrespondeFiltro(produto?.descricao, produtoFilter) ||
      String(produto?.id ?? "").includes(produtoIdFilter)
    ) &&
    (listarInativos || (!produto.grupo?.inativo && !produto.inativo))
  );

// ✅ filtro de grupo:
// - se clicou em um grupo: filtra por ele (mesmo com multi ativo)
// - senão, se multi ativo: filtra pelos ids
if (!exibirFavoritos && selectedGroup) {
  filteredProdutos = filteredProdutos.filter(p => p.grupo?.id === selectedGroup.id);
} else if (!exibirFavoritos && hiddenGroupIds.size > 0) {
  filteredProdutos = filteredProdutos.filter(p => !p.grupo?.id || !hiddenGroupIds.has(p.grupo.id));
}



  // Ordenar os produtos
  const sortedProdutos = filteredProdutos.sort((a, b) => {
    if (sortField === 'id') {
      return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
    } else if (sortField === 'descricao') {
      const descriptionA = normalizarTextoProduto(a.descricao);
      const descriptionB = normalizarTextoProduto(b.descricao);
      if (descriptionA < descriptionB) return sortDirection === 'asc' ? -1 : 1;
      if (descriptionA > descriptionB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDoubleClick = (produto) => {
    onSelectProduto(produto);
    onHide();
  };

  // Navegação por teclado nos grupos (tabela)
  const focusProductGrid = () => {
    if (!productTableRef.current) return;

    const firstSelectButton = productTableRef.current.querySelector('tbody button');

    if (firstSelectButton) {
      firstSelectButton.focus();
      return;
    }

    productTableRef.current.focus();
  };

  const handleProdutoInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      focusProductGrid();
    }
  };

  const focusProductButtonByIndex = (index) => {
    if (!productTableRef.current) return;

    const buttons = productTableRef.current.querySelectorAll('tbody button');
    const targetButton = buttons[index];

    if (targetButton) {
      targetButton.focus();
      targetButton.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleProductButtonKeyDown = (event, index, visibleItemsLength) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();

    const nextIndex = event.key === 'ArrowDown'
      ? Math.min(index + 1, visibleItemsLength - 1)
      : Math.max(index - 1, 0);

    focusProductButtonByIndex(nextIndex);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (gruposVisiveis.length === 0) {
        setSelectedGroup(null);
        setSelectedGroupIndex(-1);
        return;
      }

      let newIndex = selectedGroup ? selectedGroupIndex : -1;

      if (e.key === 'ArrowUp') {
        newIndex = newIndex > -1 ? newIndex - 1 : gruposVisiveis.length - 1;
        } else if (e.key === 'ArrowDown') {
        newIndex = newIndex < gruposVisiveis.length - 1 ? newIndex + 1 : -1;
        }

        setSelectedGroupIndex(newIndex);
        setSelectedGroup(newIndex >= 0 ? gruposVisiveis[newIndex] : null);
        setExibirFavoritos(false);

      // ✅ Se a pessoa está usando multi seleção, não mexe nela
      // (pra não misturar os modos sem querer)
    }
  };

  // ==========================
  // ✅ Ações do modal de grupos
  // ==========================
  const toggleGroupId = (id) => {
    setHiddenGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const marcarTodos = () => {
    setHiddenGroupIds(new Set());
  };

  const desmarcarTodos = () => {
    setHiddenGroupIds(new Set(grupos.map(g => g.id)));
  };

  const abrirModalGrupos = () => {
    setShowGroupModal(true);
  };

  const fecharModalGrupos = () => {
    setShowGroupModal(false);
  };

  const selecionarTodos = () => {
    setExibirFavoritos(false);
    setSelectedGroup(null);
    setSelectedGroupIndex(-1);
  };

  const selecionarFavoritos = () => {
    setExibirFavoritos(true);
    setSelectedGroup(null);
    setSelectedGroupIndex(-1);
  };

  const adicionarFavorito = async (produto) => {
    if (!produto) return;

    await alteraFavorito(produto.id, true);
    setShowAdicionarFavoritoModal(false);
  };


  if (!show) return null;

  return (
    <>
    <ModalContainer show={show} variant="busca" onHide={onHide} largura="950px" contentClassName="produto-delivery-container">
          <ModalHeader title="Buscar Produto" onHide={onHide} />
          <ModalBody>
            <div className="delivery-filters">
              <div className="delivery-group-filter">
                {/* ✅ input + botão filtro */}
                <div className="grupo-filter-wrapper">
                  <ModalSearchBar label="Grupo" autoFocus={false} placeholder="Filtrar por grupo..." value={grupoFilter} onChange={(e) => setGrupoFilter(e.target.value)} />

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm grupo-filter-btn"
                    title="Selecionar grupos"
                    aria-label="Selecionar grupos"
                    onClick={abrirModalGrupos}
                  >
                    <FaFilter />
                  </button>
                </div>

                {/* ✅ badge mostrando quantos grupos estão marcados */}
                {/* {hiddenGroupIds.size > 0 && (
                  <div className="grupo-selected-badge">
                    {hiddenGroupIds.size} grupo(s) oculto(s)
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 ms-2"
                      onClick={desmarcarTodos}
                      title="Limpar seleção"
                    >
                      limpar
                    </button>
                  </div>
                )} */}
              </div>

              <div className="delivery-product-filter">
                <ModalSearchBar
                  inputRef={produtoInputRef}
                  label="Produto"
                  placeholder="Filtrar por descrição ou ID do produto..."
                  value={produtoFilter}
                  onChange={(e) => setProdutoFilter(e.target.value)}
                  onKeyDown={handleProdutoInputKeyDown}
                />
              </div>

              {exibirFavoritos && (
                <div className="delivery-favorites-filter">
                  <button
                    type="button"
                    className="btn btn-outline-secondary modal-action favoritos-button"
                    onClick={() => setShowFavoritosModal(true)}
                  >
                    <FaPlus className="me-1" />
                    Favoritos
                  </button>
                </div>
              )}

              <InputCheck id="listarInativos" label="Inativos" checked={listarInativos} onChange={(e) => setListarInativos(e.target.checked)} />
            </div>

            <div className="delivery-grid">
              <div className="group-section">
                  <ModalTable containerClassName="group-table-container" containerRef={groupTableRef}
                    className="group-table"
                    tabIndex="0"
                    onKeyDown={handleKeyDown}
                  >
                    <thead>
                      <tr>
                        <th scope="col">Grupos</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        onClick={selecionarTodos}
                        className={`grupo-todos-row ${!exibirFavoritos && !selectedGroup ? 'table-active' : ''}`}
                      >
                        <td><button type="button" aria-pressed={!exibirFavoritos && !selectedGroup}>Todos os produtos</button></td>
                      </tr>
                      <tr
                        onClick={selecionarFavoritos}
                        className={`grupo-favoritos-row ${exibirFavoritos ? 'table-active' : ''}`}
                      >
                        <td><button type="button" aria-pressed={exibirFavoritos}>Favoritos</button></td>
                      </tr>
                      {gruposVisiveis.map((grupo, index) => (
                        <tr
                          key={grupo.id}
                          onClick={() => {
                            setExibirFavoritos(false);
                            // ✅ toggle: clicou no mesmo grupo, remove seleção
                            if (selectedGroup?.id === grupo.id) {
                                setSelectedGroup(null);
                                setSelectedGroupIndex(-1);
                                return;
                            }

                            setSelectedGroup(grupo);
                            setSelectedGroupIndex(index);
                            }}

                          className={selectedGroup?.id === grupo.id ? 'table-active' : ''}
                        >
                          <td><button type="button" aria-pressed={selectedGroup?.id === grupo.id}>{grupo.descricao}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </ModalTable>
              </div>

              <div className="product-section">
                <LazyLoading
                  items={sortedProdutos}
                  resetKey={JSON.stringify([
                    produtoFilter,
                    selectedGroup?.id ?? null,
                    Array.from(hiddenGroupIds).sort((a, b) => a - b),
                    exibirFavoritos,
                    listarInativos,
                    sortField,
                    sortDirection,
                  ])}
                >
                {({ containerRef, handleScroll, visibleItems }) => (
                <ModalTable containerRef={containerRef} onContainerScroll={handleScroll}
                  containerClassName="product-table-container" tableRef={productTableRef} className="product-table"
                  tabIndex="0" emptyMessage="Nenhum produto encontrado.">
                    <thead>
                      <tr>
                        <th className="text-center delivery-id-column" aria-sort={sortField === 'id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                          <button
                            onClick={() => handleSort('id')}
                            className="sort-button"
                            type="button"
                          >
                            Id
                            {sortField === 'id' && (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                          </button>
                        </th>

                        <th aria-sort={sortField === 'descricao' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                          <button
                            onClick={() => handleSort('descricao')}
                            className="sort-button"
                            type="button"
                          >
                            Descrição
                            {sortField === 'descricao' && (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                          </button>
                        </th>

                        <th className="text-end delivery-price-column">
                          Preço
                        </th>

                        <th className="modal-select-column">
                          Selecionar
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {visibleItems.map((produto, index) => (
                        <tr
                          key={produto.id}
                          onDoubleClick={() => handleDoubleClick(produto)}
                        >
                          <td className="text-center">{produto.id}</td>
                          <td>{produto.descricao}</td>
                          <td className="text-end">{formatNumeric(produto.preco)}</td>
                          <td className="modal-select-column">
                            <ModalSelectButton label={produto.descricao} onClick={() => onSelectProduto(produto)}
                              onKeyDown={(event) => handleProductButtonKeyDown(event, index, visibleItems.length)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                </ModalTable>
                )}
                </LazyLoading>
              </div>
            </div>
          </ModalBody>
          <ModalFooter><ModalActionButton action="close" onClick={onHide}>Fechar</ModalActionButton></ModalFooter>
    </ModalContainer>

      <ModalContainer show={showFavoritosModal} variant="cadastro" onHide={() => setShowFavoritosModal(false)} largura="650px" contentClassName="produto-delivery-container">
              <ModalHeader title="Produtos favoritos" onHide={() => setShowFavoritosModal(false)} />
              <ModalBody>
                <div className="d-flex justify-content-end mb-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAdicionarFavoritoModal(true)}
                  >
                    <FaPlus className="me-1" />
                    Adicionar produto
                  </button>
                </div>

                <div className="favoritos-lista">
                  {produtosFavoritos.map(produto => (
                    <div key={produto.id} className="favorito-item">
                      <span>{produto.id} - {produto.descricao}</span>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        title="Remover dos favoritos"
                        aria-label={`Remover favorito ${produto.descricao}`}
                        onClick={() => alteraFavorito(produto.id, false)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}

                  {produtosFavoritos.length === 0 && (
                    <div className="text-muted text-center py-3">
                      Nenhum produto favorito cadastrado.
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter><ModalActionButton action="close" onClick={() => setShowFavoritosModal(false)}>Fechar</ModalActionButton></ModalFooter>
      </ModalContainer>

      <ProdutoSearch
        show={showAdicionarFavoritoModal}
        onHide={() => setShowAdicionarFavoritoModal(false)}
        onSelectProduto={adicionarFavorito}
      />

      {/* ==========================
          ✅ MODAL DE GRUPOS (CHECK)
         ========================== */}
      <ModalContainer show={showGroupModal} variant="cadastro" onHide={fecharModalGrupos} largura="520px" contentClassName="produto-delivery-container">
              <ModalHeader title="Filtrar grupos" onHide={fecharModalGrupos} icon={FaFilter} />
              <ModalBody>
                <div className="d-flex gap-2 mb-2">
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={marcarTodos}>
                    Marcar todos
                  </button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={desmarcarTodos}>
                    Desmarcar todos
                  </button>

                  <div className="ms-auto text-muted small d-flex align-items-center">
                    {grupos.filter(g => !hiddenGroupIds.has(g.id)).length}/{grupos.length}
                  </div>
                </div>

                <div className="group-checklist">
                  {grupos.map((g) => (
                    <InputCheck key={g.id} id={`delivery-grupo-${g.id}`} label={g.descricao}
                      checked={!hiddenGroupIds.has(g.id)} onChange={() => toggleGroupId(g.id)} />
                  ))}

                  {grupos.length === 0 && (
                    <div className="text-muted text-center py-2">
                      Nenhum grupo encontrado.
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter><ModalActionButton action="apply" onClick={salvarFiltroGrupos}>Aplicar</ModalActionButton></ModalFooter>
      </ModalContainer>
    </>
  );
};

export default ProdutoSearchDelivery;
